import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import api from "../../api/axios"; // Adjust path to your axios instance

const ProductCard = ({ product }) => {
  const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL;

  // Frontend calculation for the discount badge
  const price = parseFloat(product.price);
  const oldPrice = product.old_price ? parseFloat(product.old_price) : null;
  const discount = (oldPrice && oldPrice > price) 
    ? Math.round(((oldPrice - price) / oldPrice) * 100) 
    : 0;

  // Correct image source handling to prevent double /storage/ pathing
  const imageSrc = product.main_image.startsWith('/storage') 
    ? `${BACKEND_BASE}${product.main_image}` 
    : `${BACKEND_BASE}/storage/${product.main_image}`;

  return (
    <Link 
      to={`/product/${product.slug}`} 
      className="group flex flex-col bg-white p-4 transition-all duration-300 hover:shadow-xl border border-transparent hover:border-gray-100"
    >
      {/* Top Section: Discount Badge */}
      <div className="relative mb-4 h-48 w-full overflow-hidden sm:h-64">
        {discount > 0 && (
          <div className="absolute top-0 left-0 z-10 bg-[#ffaa00] px-2 py-1 text-[10px] font-bold text-white uppercase tracking-wider">
            Economisez {discount}%
          </div>
        )}
        
        <img 
          src={imageSrc} 
          alt={product.title} 
          className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-105"
        />
      </div>

      {/* Info Section */}
      <div className="flex flex-col text-center mt-auto">
        <h3 className="font-raleway text-sm font-medium text-black line-clamp-2 min-h-[40px] mb-2 uppercase tracking-tight group-hover:text-[#ffaa00] transition-colors">
          {product.title}
        </h3>
        
        <div className="flex flex-col items-center gap-1">
          <span className="font-raleway text-lg font-bold text-[#ffaa00]">
            {product.price} <span className="text-xs">dh</span>
          </span>
          {product.old_price && (
            <span className="font-raleway text-sm text-[#8A8A89] line-through">
              {product.old_price} dh
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

const ProductGrid = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);


  
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await api.get('/products');
        if (res.data.success) {
          // 1. Get the raw data from the response
          const allData = res.data.data;

          // 2. Limit to the last 10 products
          // If your API doesn't return newest first, use [...allData].reverse().slice(0, 10)
          const limitedProducts = allData.slice(0, 10);

          setProducts(limitedProducts);
        }
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);



  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="animate-spin text-[#ffaa00]" size={40} />
      </div>
    );
  }

  return (
    <div className="py-12 px-2 sm:px-6 lg:px-8">
      <div className="w-full mx-auto">
        <div className="grid grid-cols-2 gap-y-10 gap-x-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>

        {products.length === 0 && (
          <div className="text-center py-20 text-gray-400 uppercase font-bold tracking-widest">
            Aucun produit trouvé
          </div>
        )}

        <div className="mt-12 flex justify-center px-4">
          <Link 
            to="/nos-produits"
            className="group relative inline-flex items-center justify-center overflow-hidden border-2 border-[#ffaa00] px-10 py-3 font-raleway text-sm font-bold uppercase tracking-widest text-[#ffaa00] transition-all duration-300 hover:bg-[#ffaa00] hover:text-white w-full sm:w-auto"
          >
            <span className="relative z-10">Voir Tout</span>
            <div className="absolute inset-0 -translate-x-full bg-[#ffaa00] transition-transform duration-300 group-hover:translate-x-0"></div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProductGrid;